import { UserRespData } from "api/types/user";
import { userPage } from "api/user";
import { usePermissions } from "hooks/usePermissions";
import { CONST } from "models/constants";
import defineUserTablecolumns, { UserTableModel } from "models/tables/userPage";
import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { accountSiteList } from "store/appSelectors";

import AddUserDialog from "widgets/dialogs/addUser";
import GenericButton from "widgets/controls/buttons/generic";
import Combobox from "widgets/shared/inputs/combobox";
import { SearchInput } from "widgets/shared/inputs/search";
import Table from "widgets/controls/tables/genericTable";
import { MdRefresh } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import { useTranslation } from "react-i18next";
import { getUsersListFilters } from "store/page/pageSelectors";
import { setUsersPageFilters, UsersPageFilters } from "store/page/pageSlice";

const Users = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const columns = defineUserTablecolumns((info) => {
    const row = info.row.original;
    handleRowClick(row);
  });
  const userInfo = useSelector(accountSiteList, shallowEqual);
  const { canNavigate, availableRoles } = usePermissions();

  // Get initial filters from store once
  const initialFilters = useSelector(getUsersListFilters);

  // Local state for filters - initialized once from Redux
  const [filters, setFilters] = useState<UsersPageFilters>(
    () => initialFilters
  );

  const [dataSource, setDataSource] = useState<UserRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dialog, setDialog] = useState(false);

  const handleRowClick = (row: UserTableModel) => {
    if (canNavigate(row.role)) {
      // User has higher role, proceed to navigate
      navigate(`/admin/users/user?id=${row.id}`);
    } else {
      // Handle restricted access
      notifySimple(t("notification.warning.permission"), "warning");
    }
  };

  const updatePage = () => {
    setDialog(false);
    fetchData();
  };

  const fetchData = useCallback(async () => {
    const { paginationState, sortingState, classes, roles, search } = filters;
    setIsLoading(true);
    const { data, error, isLoading } = await userPage({
      search: search,
      pageIndex: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      desc: sortingState[0].desc,
      class: classes || "",
      roles: roles || "",
    });
    setIsLoading(isLoading);
    if (!error) {
      setDataSource(data.users || []);
      setFilters((prevFilters) => ({
        ...prevFilters,
        paginationState: {
          pageIndex: data.pageIndex - 1,
          pageSize: paginationState.pageSize,
        },
        totCount: data.totalCount,
        pageCount: data.totalPages,
      }));

      dispatch(
        setUsersPageFilters({
          paginationState: { pageIndex: 0, pageSize: 10 },
          sortingState: [{ desc: true, id: "name" }],
          totCount: 0,
          pageCount: 0,
          search: search,
          classes: classes,
          roles: roles,
        })
      );
    }
  }, [
    filters.paginationState.pageIndex,
    filters.paginationState.pageSize,
    filters.sortingState,
    filters.classes,
    filters.roles,
    filters.search,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div>
      <AddUserDialog
        isOpen={dialog}
        onClose={() => setDialog(false)}
        onSuccess={updatePage}
      />

      <Table
        filterbar={
          <div className="flex h-full w-full flex-col gap-y-3">
            <div className="flex justify-end">
              <CircleButton
                onClick={() => {
                  updatePage();
                }}
                icon={MdRefresh}
                size="md"
                color="secondary"
                variant="ghost"
                tooltip={t("tooltip.refresh")}
                tooltipPlacement="bottom"
                disabled={isLoading}
              />
              <GenericButton
                onClick={() => {
                  setDialog(true);
                }}
                type="button"
                text={t("button.action.add")}
                size="md"
                rounded="rounded-xl"
                permissionRole={CONST.ROLE_MANAGER}
                disabled={isLoading}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <div className="col-span-1">
                <Combobox
                  id="user-select-role"
                  placeholder={t("form.placeholder.select.role")}
                  variant="solid"
                  color="secondary"
                  value={filters.roles}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      roles: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={availableRoles()}
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-1">
                <Combobox
                  id="user-select-site"
                  placeholder={t("form.placeholder.select.site")}
                  variant="solid"
                  filter
                  color="secondary"
                  value={filters.classes}
                  onChange={(item) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      classes: item,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  items={userInfo}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                  disabled={isLoading}
                />
              </div>
              <div className="col-span-1 md:col-span-2">
                <SearchInput
                  onChange={(filters) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      search: filters,
                      paginationState: {
                        pageIndex: 0,
                        pageSize: prevFilters.paginationState.pageSize,
                      },
                    }));
                  }}
                  type="text"
                  value={filters.search}
                  sizes="md"
                  rounded="rounded-xl"
                  color="secondary"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        }
        loading={isLoading}
        data={dataSource}
        columns={columns}
        pagination={filters.paginationState}
        paginationOptions={{
          onPaginationChange: (pagination) => {
            const page =
              typeof pagination === "function"
                ? pagination(filters.paginationState)
                : pagination;
            setFilters((prevFilters) => ({
              ...prevFilters,
              paginationState: page,
            }));
          },
          rowCount: filters.totCount,
          pageCount: filters.pageCount,
        }}
        sorting={filters.sortingState}
        onSortingChange={(updaterOrValue) => {
          const newSortingState =
            typeof updaterOrValue === "function"
              ? updaterOrValue(filters.sortingState)
              : updaterOrValue;
          if (newSortingState && newSortingState.length > 0) {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: newSortingState,
            }));
          } else {
            setFilters((prevFilters) => ({
              ...prevFilters,
              sortingState: [{ id: "name", desc: false }],
            }));
          }
        }}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default Users;
